import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks';
import { openOverlay, type OutputPreviewOverlayData } from '@/features/adp/state/overlaySlice';
import {
  outputPreviewFormSchema,
  type OutputPreviewFormValues,
} from '@/features/adp/schemas/OutputPreview';
import { useSourceData } from '@/features/adp/hooks/useSourceData';
import { useTargetData } from '@/features/adp/hooks/useTargetData';
import { DataSourceType } from '@/features/adp/types/data-source.types';
import {
  buildColumnsFromCreatedFiles,
  resolveCreatedFilesPayload,
  extractDcOverrides,
  extractRenameOverrides,
  extractDerivedColumns,
  extractJoinDerivedColumns,
  extractDropFieldsColumns,
  extractTransposeIgnoreColumns,
  extractAggregationReplayProjection,
  isOutputPreviewStringType,
} from '@/features/adp/components/destination/dialogs/output-preview/outputPreview.utils';
import { getUpstreamNodeIds } from '@/features/adp/components/canvas/canvas.utils';
import { store } from '@/app/store';
import {
  buildPhysicalTableCreatePayload,
  getDestinationDatabaseRecordForPhysicalTable,
} from '@/features/adp/components/destination/dialogs/output-preview/buildPhysicalTablePayload';
import { buildTargetTableBatchPayload } from '@/features/adp/components/destination/dialogs/output-preview/buildTargetTableBatchPayload';
import type { LoadTypeFormData } from '@/features/adp/schemas/LoadType';
import type { CreateTargetResponse } from '@/features/adp/services/destination/destination.types';
import {
  useCreatePhysicalTableMutation,
  useCreateTargetTableBatchMutation,
} from '@/features/adp/mutations/useDestinationMutations';
import { Button } from '@/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/form';
import { Label } from '@/ui/label';
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog';
import {
  ChevronLeft,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { cn } from '@/shared/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './OutputPreview.module.scss';
import { OUTPUT_PREVIEW_TABLE_COLUMNS } from './outputPreviewTable.utils';

const isCreateTargetResponseCandidate = (value: unknown): value is CreateTargetResponse => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.source_name === 'string' &&
    typeof candidate.table_name === 'string' &&
    typeof candidate.path === 'string'
  );
};

const getHeaderCellClassName = (columnId: string) => {
  if (columnId === 'select') {
    return styles.headerCell_select;
  }

  if (columnId === 'isPrimaryKey') {
    return styles.headerCell_primaryKey;
  }

  if (columnId === 'fieldLength') {
    return styles.headerCell_length;
  }

  return styles.headerCell_default;
};

const getBodyCellClassName = (columnId: string) => {
  if (columnId === 'select') {
    return styles.bodyCell_select;
  }

  if (columnId === 'isPrimaryKey') {
    return styles.bodyCell_primaryKey;
  }

  if (columnId === 'fieldLength') {
    return styles.bodyCell_length;
  }

  return styles.bodyCell_default;
};

const OutputPreview = () => {
  const { type, isOpen } = useAppSelector(state => state.overlay);
  const isOpenForOutputPreview = isOpen && type === 'output-preview';
  const outputPreviewOverlayData = useAppSelector(state =>
    state.overlay.type === 'output-preview' && state.overlay.isOpen
      ? (state.overlay.data as OutputPreviewOverlayData)
      : null
  );
  const dispatch = useAppDispatch();
  const { getSourceDataByNodeId } = useSourceData();
  const { updateTargetData, getTargetDataByNodeId } = useTargetData();
  const createPhysicalTableMutation = useCreatePhysicalTableMutation();
  const createTargetTableBatchMutation = useCreateTargetTableBatchMutation();

  const form = useForm<OutputPreviewFormValues>({
    resolver: zodResolver(outputPreviewFormSchema),
    defaultValues: {
      createPhysicalTable: 'no',
      columns: [],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'columns',
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  /** One-shot guard: only reset the form when a different node opens the
   * sheet (or it reopens after a close). Without this, an unrelated re-render
   * that re-runs the effect would wipe the user's in-progress column edits. */
  const lastInitializedNodeIdRef = useRef<string | null>(null);

  const nodeId = outputPreviewOverlayData?.nodeId ?? null;
  const dataSourceTypeRaw = outputPreviewOverlayData?.data_source_type ?? null;
  const dataSourceInstanceRaw = outputPreviewOverlayData?.data_source_instance ?? null;
  const isRdbms = dataSourceTypeRaw?.trim().toUpperCase() === DataSourceType.RDBMS;
  const isFileSystemDestination =
    dataSourceTypeRaw?.trim().toUpperCase() === DataSourceType.FILESYSTEM;
  const isExcelDestination = dataSourceInstanceRaw?.trim().toLowerCase() === 'excel';
  const skipsLoadTypeForm = isExcelDestination || isFileSystemDestination;

  /**
   * On revisit, a prior OutputPreview save has already persisted `target_table`
   * (the create-target-table-batch response) into the node's session data.
   * Re-running the create-physical-table POST against the same destination
   * triggers a backend 500 ("relation already exists"), and the
   * create-target-table-batch POST is similarly redundant. When this flag is
   * true, the Save button becomes "Continue" and `onSubmit` skips both POSTs.
   */
  const persistedTargetData = nodeId ? getTargetDataByNodeId(nodeId)?.data : null;
  const persistedTargetTable = persistedTargetData?.target_table;
  const isAlreadyCreated = Array.isArray(persistedTargetTable) && persistedTargetTable.length > 0;
  /**
   * Set right after `/physical-tables` POST returns 200 (before the
   * subsequent `/target-tables/batch` runs). Without this, a partial save
   * (physical succeeded, batch failed) would leave `target_table` empty so
   * the next retry re-POSTs `/physical-tables` and 500s on the duplicate.
   */
  const physicalTableCreated = persistedTargetData?.physical_table_created === true;

  useEffect(() => {
    if (!isOpenForOutputPreview || nodeId == null) return;

    const { nodes, edges } = store.getState().canvas.present;

    // All upstream node IDs (any type) — used to find source files
    const upstreamIds = getUpstreamNodeIds(nodeId, edges);

    // Subset that are transformation nodes — used to extract DC overrides
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const upstreamTransformationIds = upstreamIds.filter(
      id => nodeMap.get(id)?.type === 'transformation'
    );
    const upstreamIntegrationIds = upstreamIds.filter(
      id => nodeMap.get(id)?.type === 'integration'
    );

    const rawFiles = resolveCreatedFilesPayload(upstreamIds, getSourceDataByNodeId);
    const dcOverrides = extractDcOverrides(upstreamTransformationIds);
    const renameOverrides = extractRenameOverrides(upstreamTransformationIds);
    const baseDerivedColumns = extractDerivedColumns(upstreamTransformationIds);
    const joinDerivedColumns = extractJoinDerivedColumns(
      upstreamIntegrationIds,
      upstreamIds,
      getSourceDataByNodeId,
    );
    const baseDropFields = extractDropFieldsColumns(upstreamTransformationIds);
    const transposeIgnoreColumns = extractTransposeIgnoreColumns(upstreamTransformationIds);
    const aggregationProjection = extractAggregationReplayProjection(
      upstreamTransformationIds,
      rawFiles,
    );
    const derivedColumns = aggregationProjection
      ? [...baseDerivedColumns, ...joinDerivedColumns, ...aggregationProjection.derivedColumns]
      : [...baseDerivedColumns, ...joinDerivedColumns];
    const dropFields = [
      ...baseDropFields,
      ...transposeIgnoreColumns,
      ...(aggregationProjection?.dropFields ?? []),
    ];

    const columns = buildColumnsFromCreatedFiles(
      rawFiles,
      dcOverrides,
      renameOverrides,
      derivedColumns,
      dropFields,
    );

    // Only seed the form on initial open for this node — re-running the
    // effect for downstream state changes shouldn't wipe in-progress edits.
    if (lastInitializedNodeIdRef.current !== nodeId) {
      form.reset({
        createPhysicalTable: isRdbms ? 'yes' : 'no',
        columns,
      });
      setPagination(p => ({ ...p, pageIndex: 0 }));
      lastInitializedNodeIdRef.current = nodeId;
    }
  }, [isOpenForOutputPreview, nodeId, isRdbms, getSourceDataByNodeId, form]);

  const columnsWatch = form.watch('columns');
  const allSelected = (columnsWatch?.length ?? 0) > 0 && columnsWatch.every(c => c.selected);
  const someSelected = columnsWatch.some(c => c.selected);

  const setAllSelected = useCallback(
    (checked: boolean) => {
      const current = form.getValues('columns');
      form.setValue(
        'columns',
        current.map(c => ({ ...c, selected: checked })),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [form]
  );

  let checkboxState: boolean | 'indeterminate' = false;

  if (allSelected) {
    checkboxState = true;
  } else if (someSelected) {
    checkboxState = 'indeterminate';
  }

  const table = useReactTable({
    data: fields,
    columns: OUTPUT_PREVIEW_TABLE_COLUMNS,
    meta: {
      fields,
      form,
      checkboxState,
      setAllSelected,
    },
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    getRowId: row => row.id,
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset({
        createPhysicalTable: 'no',
        columns: [],
      });
      // Clear the init guard so the next open seeds fresh state — including
      // when the user reopens for the same node after closing.
      lastInitializedNodeIdRef.current = null;
      dispatch(closeOverlayAsync());
    }
  };

  const isSubmitting =
    createPhysicalTableMutation.isPending || createTargetTableBatchMutation.isPending;

  const onSubmit = async (values: OutputPreviewFormValues) => {
    if (nodeId == null) return;

    const createPhysicalTable = isRdbms ? values.createPhysicalTable === 'yes' : false;
    const targetData = getTargetDataByNodeId(nodeId)?.data;
    const loadTypeForm = targetData?.loadTypeForm as LoadTypeFormData | undefined;
    const createTargetResponse = isCreateTargetResponseCandidate(targetData)
      ? targetData
      : undefined;

    if (!createTargetResponse) {
      console.warn('Cannot submit output preview: missing target data');
      return;
    }

    if (!skipsLoadTypeForm && !loadTypeForm) {
      console.warn('Cannot submit output preview: missing load type form');
      return;
    }

    // Revisit short-circuit: target_table already exists in session from a
    // prior save. Re-creating the physical table would 500 ("relation already
    // exists"); re-creating the target-tables batch would duplicate. Skip both
    // and just navigate to Column Mapping.
    if (isAlreadyCreated) {
      dispatch(
        openOverlay({
          type: 'column-mapping',
          data: { nodeId },
        })
      );
      return;
    }

    try {
      // Skip when a prior partial save already created the physical table.
      // Without this, retrying after a `/target-tables/batch` failure would
      // re-POST `/physical-tables` and 500 on the duplicate.
      if (createPhysicalTable && !physicalTableCreated) {
        const destinationDatabase = getDestinationDatabaseRecordForPhysicalTable(nodeId);
        if (!destinationDatabase || typeof destinationDatabase.type !== 'string') {
          console.warn('Cannot create physical table: destination configuration missing');
          return;
        }

        if (!loadTypeForm) {
          console.warn('Cannot create physical table: missing load type form');
          return;
        }

        // Create physical table
        const physicalTablePayload = buildPhysicalTableCreatePayload({
          destinationDatabase,
          loadTypeForm,
          outputPreviewValues: values,
        });
        await createPhysicalTableMutation.mutateAsync(physicalTablePayload);
        // Persist flag immediately so the next throw doesn't strand us.
        updateTargetData(nodeId, { physical_table_created: true });
      }

      // Create target table batch
      const targetTableBatchPayload = buildTargetTableBatchPayload({
        targetData: createTargetResponse,
        loadTypeForm,
        outputPreviewValues: values,
      });
      const targetTableBatchResponse =
        await createTargetTableBatchMutation.mutateAsync(targetTableBatchPayload);

      // Update target data
      updateTargetData(nodeId, {
        outputPreview: {
          createPhysicalTable,
          columns: values.columns.map(c => ({
            sourceFileFieldId: c.sourceFileFieldId,
            fieldName: c.fieldName,
            selected: c.selected,
            fieldDataType: c.fieldDataType,
            fieldLength: isOutputPreviewStringType(c.fieldDataType) ? c.fieldLength : 0,
            isPrimaryKey: c.isPrimaryKey,
          })),
        },
        target_table: targetTableBatchResponse.created_table,
        target_columns: targetTableBatchResponse.created_columns,
      });

      dispatch(
        openOverlay({
          type: 'column-mapping',
          data: { nodeId },
        })
      );
    } catch (error) {
      console.error('Failed to submit output preview', error);
    }
  };

  return (
    <Dialog open={isOpenForOutputPreview} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader className="flex !flex-row items-center justify-start gap-2">
          <DialogClose asChild>
            <Button variant="ghost" size="icon">
              <ChevronLeft />
            </Button>
          </DialogClose>
          <div className="flex flex-col gap-0.5">
            <DialogTitle className="!mt-0 text-xl font-medium">Output Preview</DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody className="min-h-0 flex-1">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex h-full min-h-0 w-full flex-col"
            >
              {isAlreadyCreated && (
                <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  This destination table was already created. Column changes here
                  will not be saved — click <strong>Continue</strong> to proceed to
                  Column Mapping, or delete the destination node to start over.
                </div>
              )}
              {isRdbms && (
                <FormField
                  control={form.control}
                  name="createPhysicalTable"
                  render={({ field }) => (
                    <FormItem className="mb-4 flex flex-col items-end gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-end">
                      <FormLabel className="max-w-md text-right text-sm font-normal leading-snug text-gray-700 sm:text-left">
                        Do you want to create a physical table?
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="flex flex-row items-center gap-6"
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id="output-preview-physical-yes" />
                            <Label
                              htmlFor="output-preview-physical-yes"
                              className="cursor-pointer text-sm font-normal text-gray-800"
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="no" id="output-preview-physical-no" />
                            <Label
                              htmlFor="output-preview-physical-no"
                              className="cursor-pointer text-sm font-normal text-gray-800"
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage className="text-right sm:text-left" />
                    </FormItem>
                  )}
                />
              )}

              <div className="min-h-0 w-full flex-1 overflow-hidden">
                {fields.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No source columns found. Complete source file selection first.
                  </p>
                ) : (
                  <div className="h-full overflow-auto">
                    <table className="w-full table-fixed border-collapse rounded-t-lg">
                      <thead style={{ backgroundColor: '#F5F5F5' }}>
                        {table.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th
                                key={header.id}
                                className={getHeaderCellClassName(header.column.id)}
                                style={{ width: `${header.getSize()}px` }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody className="bg-white">
                        {table.getRowModel().rows.map(row => (
                          <tr key={row.id} className="border-b border-gray-200 hover:bg-gray-50">
                            {row.getVisibleCells().map(cell => (
                              <td
                                key={cell.id}
                                className={getBodyCellClassName(cell.column.id)}
                                style={{ width: `${cell.column.getSize()}px` }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {fields.length > 0 && (
                <div className="mt-1 flex items-center gap-4 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Rows per page:</span>
                    <Select
                      value={pagination.pageSize.toString()}
                      onValueChange={value => table.setPageSize(Number(value))}
                    >
                      <SelectTrigger
                        className={cn(
                          'h-9 w-8 min-w-0 shrink-0 border-0 bg-transparent px-0 py-0 shadow-none',
                          'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
                          'data-[state=open]:ring-0 data-[state=open]:ring-offset-0'
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 20].map(size => (
                          <SelectItem key={size} value={size.toString()}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1" />

                  <div className="text-sm text-gray-700">
                    <span className="font-semibold">
                      {table.getState().pagination.pageIndex *
                        table.getState().pagination.pageSize +
                        1}
                    </span>{' '}
                    to{' '}
                    <span className="font-semibold">
                      {Math.min(
                        (table.getState().pagination.pageIndex + 1) *
                          table.getState().pagination.pageSize,
                        table.getFilteredRowModel().rows.length
                      )}
                    </span>{' '}
                    of{' '}
                    <span className="font-semibold">{table.getFilteredRowModel().rows.length}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                      className="h-auto p-1"
                    >
                      <ChevronsLeft className="size-5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      className="h-auto p-1"
                    >
                      <ChevronLeftIcon className="size-5" />
                    </Button>
                    <span className="px-2 text-sm text-gray-700">
                      Page{' '}
                      <span className="font-bold">{table.getState().pagination.pageIndex + 1}</span>{' '}
                      of <span className="font-bold">{table.getPageCount()}</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      className="h-auto p-1"
                    >
                      <ChevronRightIcon className="size-5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                      disabled={!table.getCanNextPage()}
                      className="h-auto p-1"
                    >
                      <ChevronsRight className="size-5" />
                    </Button>
                  </div>
                </div>
              )}

              <DialogFooter className="!mt-4 !pr-0">
                <Button
                  type="submit"
                  disabled={fields.length === 0 || nodeId == null || isSubmitting}
                >
                  {isSubmitting
                    ? 'Saving...'
                    : isAlreadyCreated
                      ? 'Continue'
                      : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default OutputPreview;
